import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Building2, User, Mail, Phone, MapPin, Shield, 
  ArrowRight, CheckCircle, Clock, AlertCircle, Loader2, KeyRound 
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import ForgotPasswordDialog from "@/components/ForgotPasswordDialog";

const FACILITY_TYPES = [
  { id: "hospital", name: "Hospital" },
  { id: "snf", name: "Skilled Nursing Facility" },
  { id: "alf", name: "Assisted Living Facility" },
  { id: "home_health", name: "Home Health Agency" },
  { id: "physician_office", name: "Physician Office" },
  { id: "hospice", name: "Hospice Agency" },
  { id: "dialysis", name: "Dialysis Center" },
  { id: "rehabilitation", name: "Rehabilitation Center" },
  { id: "other", name: "Other Healthcare Facility" }
];

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "DC"
];

export default function FacilityAuth() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("login");
  const [isLoading, setIsLoading] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  
  // Login form
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  
  // Registration form
  const [formData, setFormData] = useState({
    facility_name: "",
    facility_type: "",
    npi_number: "",
    tax_id: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    phone: "",
    fax: "",
    website: "",
    contact_name: "",
    contact_title: "",
    contact_email: "",
    contact_phone: "",
    email: "",
    password: "",
    confirmPassword: "",
    medicare_certified: false,
    medicaid_certified: false,
    joint_commission: false
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await api.post("/facility/login", {
        email: loginEmail,
        password: loginPassword
      });
      
      if (response.data.success) {
        localStorage.setItem("facility_token", response.data.token);
        localStorage.setItem("facility_data", JSON.stringify(response.data.facility));
        toast.success("Login successful!");
        navigate("/facility/dashboard");
      } else {
        toast.error(response.data.message);
        if (response.data.status === "pending") {
          setRegistrationStatus("pending");
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setIsLoading(true);
    
    try {
      const { confirmPassword, ...registrationData } = formData;
      const response = await api.post("/facility/register", registrationData);
      
      if (response.data.success) {
        setRegistrationStatus("submitted");
        toast.success("Registration submitted successfully!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setIsLoading(false);
    }
  };

  if (registrationStatus === "submitted") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        
        <div className="container mx-auto px-4 py-16 max-w-2xl">
          <Card className="text-center">
            <CardContent className="pt-12 pb-8">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                <Clock className="w-10 h-10 text-primary" />
              </div>
              <h2 className="text-2xl font-bold mb-4">{t("facility.pending.title")}</h2>
              <p className="text-muted-foreground mb-6">
                {t("facility.pending.message")}
              </p>
              <Alert className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {t("facility.pending.notice")}
                </AlertDescription>
              </Alert>
              <div className="flex gap-4 justify-center">
                <Button variant="outline" onClick={() => navigate("/")}>
                  {t("common.returnHome")}
                </Button>
                <Button onClick={() => {
                  setRegistrationStatus(null);
                  setActiveTab("login");
                }}>
                  {t("facility.goToLogin")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      
      
      <div className="container mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <Badge className="mb-4" variant="secondary">
            <Building2 className="w-3 h-3 mr-1" /> {t("facility.portal.partnerPortal")}
          </Badge>
          <h1 className="text-4xl font-bold mb-4">{t("facility.portal.title")}</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t("facility.portal.subtitle")}
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 mb-8">
              <TabsTrigger value="login" data-testid="facility-login-tab">
                Sign In
              </TabsTrigger>
              <TabsTrigger value="register" data-testid="facility-register-tab">
                Register Your Facility
              </TabsTrigger>
            </TabsList>

            {/* Login Tab */}
            <TabsContent value="login">
              <Card>
                <CardHeader>
                  <CardTitle>{t("facility.signIn.title")}</CardTitle>
                  <CardDescription>
                    {t("facility.signIn.description")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {registrationStatus === "pending" && (
                    <Alert className="mb-6">
                      <Clock className="h-4 w-4" />
                      <AlertDescription>
                        {t("facility.pending.pendingApproval")}
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">{t("facility.email")}</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="facility@example.com"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        required
                        data-testid="facility-login-email"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="login-password">{t("facility.password")}</Label>
                        <Button 
                          type="button" 
                          variant="link" 
                          className="px-0 h-auto text-xs text-primary"
                          onClick={() => setForgotPasswordOpen(true)}
                          data-testid="facility-forgot-password"
                        >
                          <KeyRound className="w-3 h-3 mr-1" />
                          {t("facility.forgotPassword")}
                        </Button>
                      </div>
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        required
                        data-testid="facility-login-password"
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full" 
                      disabled={isLoading}
                      data-testid="facility-login-submit"
                    >
                      {isLoading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("facility.signingIn")}</>
                      ) : (
                        <>{t("facility.signIn")} <ArrowRight className="w-4 h-4 ml-2" /></>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
              
              {/* Forgot Password Dialog */}
              <ForgotPasswordDialog 
                open={forgotPasswordOpen} 
                onOpenChange={setForgotPasswordOpen}
                userType="facility"
              />
            </TabsContent>

            {/* Registration Tab */}
            <TabsContent value="register">
              <Card>
                <CardHeader>
                  <CardTitle>{t("facility.register.title")}</CardTitle>
                  <CardDescription>
                    {t("facility.register.description")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleRegister} className="space-y-8">
                    {/* Facility Information */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-lg font-semibold">
                        <Building2 className="w-5 h-5 text-primary" />
                        {t("facility.info.title")}
                      </div>
                      
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label>{t("facility.info.name")} *</Label>
                          <Input
                            placeholder="General Hospital"
                            value={formData.facility_name}
                            onChange={(e) => handleInputChange("facility_name", e.target.value)}
                            required
                            data-testid="facility-name-input"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>{t("facility.info.type")} *</Label>
                          <Select 
                            value={formData.facility_type} 
                            onValueChange={(v) => handleInputChange("facility_type", v)}
                          >
                            <SelectTrigger data-testid="facility-type-select">
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                              {FACILITY_TYPES.map(type => (
                                <SelectItem key={type.id} value={type.id}>
                                  {type.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="space-y-2">
                          <Label>{t("facility.info.npi")}</Label>
                          <Input
                            placeholder="1234567890"
                            value={formData.npi_number}
                            onChange={(e) => handleInputChange("npi_number", e.target.value)}
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>{t("facility.info.taxId")}</Label>
                          <Input
                            placeholder="XX-XXXXXXX"
                            value={formData.tax_id}
                            onChange={(e) => handleInputChange("tax_id", e.target.value)}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Address */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-lg font-semibold">
                        <MapPin className="w-5 h-5 text-primary" />
                        {t("facility.address.title")}
                      </div>
                      
                      <div className="space-y-2">
                        <Label>{t("facility.address.street")} *</Label>
                        <Input
                          placeholder="123 Healthcare Drive"
                          value={formData.address}
                          onChange={(e) => handleInputChange("address", e.target.value)}
                          required
                        />
                      </div>
                      
                      <div className="grid gap-4 md:grid-cols-3">
                        <div className="space-y-2">
                          <Label>{t("facility.address.city")} *</Label>
                          <Input
                            placeholder="Falls Church"
                            value={formData.city}
                            onChange={(e) => handleInputChange("city", e.target.value)}
                            required
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>{t("facility.address.state")} *</Label>
                          <Select 
                            value={formData.state} 
                            onValueChange={(v) => handleInputChange("state", v)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="State" />
                            </SelectTrigger>
                            <SelectContent>
                              {US_STATES.map(state => (
                                <SelectItem key={state} value={state}>{state}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="space-y-2">
                          <Label>{t("facility.address.zip")} *</Label>
                          <Input
                            placeholder="22041"
                            value={formData.zip_code}
                            onChange={(e) => handleInputChange("zip_code", e.target.value)}
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="grid gap-4 md:grid-cols-3">
                        <div className="space-y-2">
                          <Label>{t("facility.address.phone")} *</Label>
                          <Input
                            placeholder="(703) 555-0100"
                            value={formData.phone}
                            onChange={(e) => handleInputChange("phone", e.target.value)}
                            required
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>{t("facility.address.fax")}</Label>
                          <Input
                            placeholder="(703) 555-0101"
                            value={formData.fax}
                            onChange={(e) => handleInputChange("fax", e.target.value)}
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>{t("facility.address.website")}</Label>
                          <Input
                            placeholder="www.facility.com"
                            value={formData.website}
                            onChange={(e) => handleInputChange("website", e.target.value)}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Primary Contact */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-lg font-semibold">
                        <User className="w-5 h-5 text-primary" />
                        {t("facility.contact.title")}
                      </div>
                      
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label>{t("facility.contact.name")} *</Label>
                          <Input
                            placeholder="Jane Smith"
                            value={formData.contact_name}
                            onChange={(e) => handleInputChange("contact_name", e.target.value)}
                            required
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>{t("facility.contact.titleField")} *</Label>
                          <Input
                            placeholder="Case Manager"
                            value={formData.contact_title}
                            onChange={(e) => handleInputChange("contact_title", e.target.value)}
                            required
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>{t("facility.contact.email")} *</Label>
                          <Input
                            type="email"
                            placeholder="jsmith@facility.com"
                            value={formData.contact_email}
                            onChange={(e) => handleInputChange("contact_email", e.target.value)}
                            required
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>{t("facility.contact.phone")} *</Label>
                          <Input
                            placeholder="(703) 555-0102"
                            value={formData.contact_phone}
                            onChange={(e) => handleInputChange("contact_phone", e.target.value)}
                            required
                          />
                        </div>
                      </div>
                    </div>

                    {/* Account Credentials */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-lg font-semibold">
                        <Shield className="w-5 h-5 text-primary" />
                        {t("facility.account.title")}
                      </div>
                      
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2 md:col-span-2">
                          <Label>{t("facility.account.email")} *</Label>
                          <Input
                            type="email"
                            placeholder="referrals@facility.com"
                            value={formData.email}
                            onChange={(e) => handleInputChange("email", e.target.value)}
                            required
                            data-testid="facility-email-input"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>{t("facility.account.password")} *</Label>
                          <Input
                            type="password"
                            placeholder="••••••••"
                            value={formData.password}
                            onChange={(e) => handleInputChange("password", e.target.value)}
                            required
                            minLength={8}
                            data-testid="facility-password-input"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>{t("facility.account.confirm")} *</Label>
                          <Input
                            type="password"
                            placeholder="••••••••"
                            value={formData.confirmPassword}
                            onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                            required
                          />
                        </div>
                      </div>
                    </div>

                    {/* Certifications */}
                    <div className="space-y-4">
                      <div className="text-lg font-semibold">{t("facility.certifications.title")}</div>
                      
                      <div className="flex flex-wrap gap-6">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="medicare"
                            checked={formData.medicare_certified}
                            onCheckedChange={(v) => handleInputChange("medicare_certified", v)}
                          />
                          <Label htmlFor="medicare">{t("facility.certifications.medicare")}</Label>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="medicaid"
                            checked={formData.medicaid_certified}
                            onCheckedChange={(v) => handleInputChange("medicaid_certified", v)}
                          />
                          <Label htmlFor="medicaid">{t("facility.certifications.medicaid")}</Label>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="jc"
                            checked={formData.joint_commission}
                            onCheckedChange={(v) => handleInputChange("joint_commission", v)}
                          />
                          <Label htmlFor="jc">{t("facility.certifications.jointCommission")}</Label>
                        </div>
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full" 
                      size="lg"
                      disabled={isLoading}
                      data-testid="facility-register-submit"
                    >
                      {isLoading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("facility.submitting")}</>
                      ) : (
                        <>{t("facility.submitRegistration")} <ArrowRight className="w-4 h-4 ml-2" /></>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
      
      
    </div>
  );
}
